<script lang="ts">
  import { api } from '$shared/api';
  import { navigate } from '$lib/router.svelte';
  import { addToast } from '$shared/toasts.svelte';
  import { trackOnboardingStyles, trackOnboardingComplete } from '$lib/analytics.svelte';
  import { getUser, fetchCurrentUser, logout } from '$lib/auth.svelte';
  import {
    BIRTH_MONTHS,
    NEUTRAL_REFUSAL,
    birthYears,
    composeBirthMonth,
    isAgeRefusal,
    signupHoldActive,
    startSignupHold,
  } from '$lib/ageScreen';
  import { connectAppleMusic } from '$lib/appleMusic.svelte';
  import { consumeReturnTo } from '$lib/utils/returnTo';

  interface Style {
    key: string;
    label: string;
    artists?: string[];
  }

  interface GenreGroup {
    genre_id: number;
    genre_name: string;
    styles: Style[];
  }

  const MAX_PICKS = 5;

  const genreColors: Record<string, string> = {
    Electronic: 'var(--cyan)',
    Pop: 'var(--pink)',
    Rock: 'var(--purple)',
    'Hip Hop': '#ff9800',
    Jazz: '#9c27b0',
    Country: '#795548',
    'Funk & Soul': '#e91e63',
    Classical: '#607d8b',
    Latin: '#ff5722',
    Reggae: '#4caf50',
    Blues: '#2196f3',
    'Stage & Screen': '#ffc107',
    'Non-Music': '#9e9e9e',
    'Brass & Military': '#827717',
  };

  let genres = $state<GenreGroup[]>([]);
  let selected = $state<Set<string>>(new Set());
  let loading = $state(true);
  let submitting = $state(false);
  let error = $state('');
  let celebrating = $state(false);
  let spotifyLoading = $state(false);
  let spotifySuggested = $state(false);
  let appleLoading = $state(false);
  let appleSuggested = $state(false);
  let connectingSpotifyOnb = $state(false);
  // Master-genre-first: null = showing genre picker; set = showing styles for that genre
  let activeGenreId = $state<number | null>(null);

  // Welcome step (DOB + privacy + optional username claim) — shown when an
  // OAuth-created user hasn't yet provided the consent fields we couldn't
  // capture during the OAuth round-trip.
  const initialUser = getUser();
  const needsWelcome = !initialUser?.privacy_version_accepted || initialUser?.default_username === true;
  let step = $state<'welcome' | 'genres'>(needsWelcome ? 'welcome' : 'genres');

  const years = birthYears();

  let birthYear = $state('');
  let birthMonth = $state('');
  let holdActive = $state(signupHoldActive());
  let privacyAccepted = $state(false);
  let claimUsername = $state(initialUser?.default_username ? '' : '');
  let welcomeError = $state(signupHoldActive() ? NEUTRAL_REFUSAL : '');
  let welcomeSubmitting = $state(false);

  let selectedCount = $derived(selected.size);
  let canSubmit = $derived(selectedCount >= 1 && selectedCount <= MAX_PICKS && !submitting);
  let showUsernameClaim = $derived(initialUser?.default_username === true);
  const alreadyHasTaste = $derived(!!(getUser()?.spotify_connected_at || getUser()?.apple_music_connected_at));
  let activeGroup = $derived(genres.find((g) => g.genre_id === activeGenreId) ?? null);

  $effect(() => {
    if (step === 'genres') fetchGenres();
  });

  $effect(() => {
    if (sessionStorage.getItem('music_just_connected') === 'spotify') {
      sessionStorage.removeItem('music_just_connected');
      loadSpotifyStyles();
    }
  });

  function signOut() {
    logout();
  }

  async function submitWelcome(e: Event) {
    e.preventDefault();
    welcomeError = '';

    if (holdActive) {
      welcomeError = NEUTRAL_REFUSAL;
      return;
    }
    const birth = composeBirthMonth(birthYear, birthMonth);
    if (!birth) {
      welcomeError = 'Please enter your birth month and year.';
      return;
    }
    if (!privacyAccepted) {
      welcomeError = 'Please accept the Terms and Privacy Policy.';
      return;
    }

    const trimmedUsername = claimUsername.trim();
    if (showUsernameClaim && trimmedUsername && !/^[a-zA-Z0-9_]{2,30}$/.test(trimmedUsername)) {
      welcomeError = 'Username must be 2-30 characters (letters, numbers, underscores).';
      return;
    }

    welcomeSubmitting = true;
    try {
      await api.put('/users/current/onboarding', {
        birth_month: birth,
        privacy_accepted: true,
        username: trimmedUsername || undefined,
      });
      // Refresh the in-memory user so navigating elsewhere reflects the new
      // privacy_version_accepted / username state.
      await fetchCurrentUser();
      step = 'genres';
    } catch (err) {
      const msg = (err as Error).message || 'Could not save your details. Try again.';
      const reason = (err as { data?: { reason?: string; error?: { reason?: string } } }).data;
      if (isAgeRefusal(msg) || reason?.reason === 'age_ineligible' || reason?.error?.reason === 'age_ineligible') {
        startSignupHold();
        holdActive = true;
        welcomeError = NEUTRAL_REFUSAL;
      } else {
        welcomeError = msg;
      }
    } finally {
      welcomeSubmitting = false;
    }
  }

  async function fetchGenres() {
    try {
      genres = await api.get<GenreGroup[]>('/users/styles');
    } catch {
      error = 'Failed to load genres. Please refresh.';
    } finally {
      loading = false;
    }
  }

  function toggle(key: string) {
    if (submitting) return;
    const next = new Set(selected);
    if (next.has(key)) {
      next.delete(key);
    } else if (next.size < MAX_PICKS) {
      next.add(key);
    }
    selected = next;
  }

  async function submit() {
    if (!canSubmit) return;
    submitting = true;
    error = '';

    try {
      await api.put('/users/current/styles', {
        styles: [...selected],
      });
      trackOnboardingStyles([...selected]);
      trackOnboardingComplete();
      celebrating = true;
      submitting = false;
      // Honor a destination stashed before the auth detour (guard redirect / OAuth /
      // password login that required onboarding first); otherwise land on /duels.
      const dest = consumeReturnTo() || '/duels';
      setTimeout(() => navigate(dest), 2200);
    } catch (e) {
      error = (e as Error).message || 'Something went wrong. Please try again.';
      addToast(error, 'error');
      submitting = false;
    }
  }

  async function loadSpotifyStyles() {
    spotifyLoading = true;
    try {
      const res = await api.get<{ styles: string[] }>('/users/current/spotify-genres');
      if (res.styles.length > 0) {
        const next = new Set(selected);
        for (const s of res.styles) {
          if (next.size < MAX_PICKS) next.add(s);
        }
        selected = next;
        spotifySuggested = true;
        addToast(`Found ${res.styles.length} styles from your Spotify!`, 'follow');
      } else {
        addToast('No matching styles found. Pick your own!', 'join');
      }
    } catch {
      addToast('Could not connect to Spotify', 'error');
    } finally {
      spotifyLoading = false;
    }
  }

  async function loadAppleStyles() {
    appleLoading = true;
    try {
      const res = await api.get<{ styles: string[] }>('/users/current/apple-genres');
      const next = new Set(selected);
      for (const s of res.styles) {
        if (next.size < MAX_PICKS) next.add(s);
      }
      selected = next;
      appleSuggested = true;
      if (res.styles.length) {
        addToast(`Found ${res.styles.length} styles from your Apple Music!`, 'follow');
      } else {
        addToast('No matching styles found. Pick your own!', 'join');
      }
    } catch (e) {
      console.error('Apple import failed:', e);
      addToast('Could not import from Apple Music', 'error');
    } finally {
      appleLoading = false;
    }
  }

  async function connectAppleMusicOnboarding() {
    appleLoading = true;
    try {
      await connectAppleMusic();
      await loadAppleStyles();
    } catch (e) {
      console.error('Apple Music connect failed:', e);
      addToast((e as Error).message || 'Could not connect Apple Music', 'error');
      appleLoading = false;
    }
  }

  async function connectSpotifyOnboarding() {
    connectingSpotifyOnb = true;
    try {
      const { url, state: oauthState } = await api.get<{ url: string; state: string }>('/auth/spotify/url');
      if (!url) {
        addToast('Spotify is not configured', 'error');
        connectingSpotifyOnb = false;
        return;
      }
      if (oauthState) sessionStorage.setItem('spotify_connect_state', oauthState);
      sessionStorage.setItem('spotify_connect_return', '/onboarding');
      window.location.href = url;
    } catch (e) {
      console.error('Spotify connect failed:', e);
      addToast('Could not connect Spotify', 'error');
      connectingSpotifyOnb = false;
    }
  }
</script>

<div class="onboarding">
  <div class="onboarding-inner">
    {#if celebrating}
      <div class="celebration">
        <div class="celebration-icon">&#127925;</div>
        <h2 class="celebration-heading">You're all set!</h2>
        <p class="celebration-text">Finding your first duel...</p>
        <div class="celebration-bar"><div class="celebration-fill"></div></div>
      </div>
    {:else if step === 'welcome'}
      <h1 class="heading">Welcome to Beat Duel</h1>
      <p class="subtitle">A few quick details before we find your first duel.</p>

      <form class="welcome-card" onsubmit={submitWelcome}>
        {#if welcomeError}<p class="error-msg">{welcomeError}</p>{/if}
        {#if holdActive}<button type="button" class="link-btn" onclick={signOut}>Sign out</button>{/if}

        {#if showUsernameClaim}
          <label class="welcome-field">
            <span class="welcome-label">Pick your username (optional)</span>
            <input
              class="welcome-input"
              type="text"
              bind:value={claimUsername}
              maxlength="30"
              placeholder={initialUser?.username || 'username'}
              autocomplete="username"
            />
            <span class="hint">Keep "{initialUser?.username}" or pick something new.</span>
          </label>
        {/if}

        <fieldset class="welcome-field">
          <legend class="welcome-label">Birth month</legend>
          <div class="dob-row">
            <select
              class="welcome-input dob-select"
              bind:value={birthMonth}
              required
              aria-label="Month"
              disabled={holdActive}
            >
              <option value="" disabled>Month</option>
              {#each BIRTH_MONTHS as m (m)}
                <option value={m}>{m}</option>
              {/each}
            </select>
            <select
              class="welcome-input dob-select"
              bind:value={birthYear}
              required
              aria-label="Year"
              disabled={holdActive}
            >
              <option value="" disabled>Year</option>
              {#each years as y (y)}
                <option value={y}>{y}</option>
              {/each}
            </select>
          </div>
        </fieldset>

        <label class="welcome-check">
          <input type="checkbox" bind:checked={privacyAccepted} required />
          <span>
            I agree to the
            <a href="#/terms" target="_blank" rel="noopener">Terms</a> and
            <a href="#/privacy" target="_blank" rel="noopener">Privacy Policy</a>.
          </span>
        </label>

        <button class="cta" type="submit" disabled={welcomeSubmitting || holdActive}>
          {welcomeSubmitting ? 'Saving...' : 'Continue'}
        </button>
      </form>
    {:else}
      <h1 class="heading">What's Your Sound?</h1>
      <p class="subtitle">Pick the genres you love. We'll find the perfect duel for you.</p>

      {#if loading}
        <div class="loader">
          <div class="spinner"></div>
        </div>
      {:else if error && genres.length === 0}
        <p class="error-msg">{error}</p>
      {:else}
        <div class="pick-count" class:warn={selectedCount >= MAX_PICKS}>
          {selectedCount} / {MAX_PICKS} selected
        </div>

        <div class="connect-section">
          {#if !alreadyHasTaste}
            <p class="connect-hint">Connect your music to auto-suggest your styles:</p>
            <div class="connect-buttons">
              <button
                class="btn btn-social btn-social--apple"
                type="button"
                onclick={connectAppleMusicOnboarding}
                disabled={appleLoading}
              >
                {appleLoading ? 'Connecting…' : 'Connect Apple Music'}
              </button>
              <button
                class="btn btn-social btn-social--spotify"
                type="button"
                onclick={connectSpotifyOnboarding}
                disabled={connectingSpotifyOnb}
              >
                {connectingSpotifyOnb ? 'Redirecting…' : 'Connect Spotify'}
              </button>
            </div>
          {/if}

          {#if !spotifyLoading && !spotifySuggested}
            <button class="btn-link" onclick={loadSpotifyStyles}> Import from Spotify </button>
          {:else if spotifyLoading}
            <span class="text-muted">Loading your Spotify taste…</span>
          {:else if spotifySuggested}
            <span class="text-muted">Styles suggested from your Spotify</span>
          {/if}
          {#if !appleLoading && !appleSuggested}
            <button class="btn-link" onclick={loadAppleStyles}> Import from Apple Music </button>
          {:else if appleSuggested}
            <span class="text-muted">Styles suggested from your Apple Music</span>
          {/if}
        </div>

        {#if activeGroup === null}
          <!-- Step 1: pick a master genre -->
          <div class="genre-grid">
            {#each genres as group}
              {@const color = genreColors[group.genre_name] ?? 'var(--cyan)'}
              {@const pickedCount = group.styles.filter((s) => selected.has(s.key)).length}
              <button
                class="genre-card"
                style="--accent: {color}"
                onclick={() => {
                  activeGenreId = group.genre_id;
                }}
                aria-label="Pick styles from {group.genre_name}"
              >
                <span class="genre-card-name" style="color: {color}">{group.genre_name}</span>
                {#if pickedCount > 0}
                  <span class="genre-card-badge">{pickedCount}</span>
                {/if}
              </button>
            {/each}
          </div>
        {:else}
          <!-- Step 2: pick styles within the chosen genre -->
          {@const color = genreColors[activeGroup.genre_name] ?? 'var(--cyan)'}
          <div class="genre-drill">
            <button
              class="genre-back"
              onclick={() => {
                activeGenreId = null;
              }}
              aria-label="Back to genres">← {activeGroup.genre_name}</button
            >
            <div class="chips">
              {#each activeGroup.styles as sub}
                {@const isSelected = selected.has(sub.key)}
                {@const atMax = selectedCount >= MAX_PICKS && !isSelected}
                <button
                  class="chip"
                  class:selected={isSelected}
                  class:disabled={atMax}
                  style="--accent: {color}"
                  onclick={() => toggle(sub.key)}
                  disabled={atMax}
                  aria-pressed={isSelected}
                >
                  {#if isSelected}
                    <svg class="check-icon" viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                      <path
                        fill-rule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clip-rule="evenodd"
                      />
                    </svg>
                  {/if}
                  {sub.label}
                  {#if sub.artists?.length}
                    <span class="chip-artists">{sub.artists.join(', ')}</span>
                  {/if}
                </button>
              {/each}
            </div>
          </div>
        {/if}

        {#if error}
          <p class="error-msg">{error}</p>
        {/if}

        <button class="cta" onclick={submit} disabled={!canSubmit}>
          {#if submitting}
            <span class="btn-spinner"></span>
            Creating your duel...
          {:else}
            Let's Go!
          {/if}
        </button>
      {/if}
    {/if}
  </div>
</div>

<style>
  .onboarding {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem 1rem;
    background: var(--bg);
  }

  .onboarding-inner {
    max-width: 640px;
    width: 100%;
    text-align: center;
  }

  .heading {
    font-size: 2.25rem;
    font-weight: 800;
    color: var(--text);
    margin: 0 0 0.5rem;
    letter-spacing: -0.02em;
  }

  .subtitle {
    color: var(--text-secondary);
    font-size: 1.1rem;
    margin: 0 0 2.5rem;
    line-height: 1.5;
  }

  .loader {
    display: flex;
    justify-content: center;
    padding: 4rem 0;
  }

  .spinner {
    width: 36px;
    height: 36px;
    border: 3px solid var(--surface-2);
    border-top-color: var(--cyan);
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .link-btn {
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .error-msg {
    color: #ef4444;
    font-size: 0.9rem;
    margin: 1rem 0;
  }

  /* ── Master-genre grid (step 1) ─────────────────────────────────── */
  .genre-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 0.75rem;
    text-align: left;
    margin-top: 0.5rem;
  }

  .genre-card {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.35rem;
    padding: 0.85rem 1rem;
    border: 1.5px solid var(--border);
    border-radius: 12px;
    background: transparent;
    cursor: pointer;
    transition:
      border-color 0.2s,
      background 0.2s;
  }

  .genre-card:hover {
    border-color: var(--accent);
    background: rgba(255, 255, 255, 0.04);
  }

  .genre-card-name {
    font-size: 0.9rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }

  .genre-card-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.35rem;
    border-radius: 100px;
    background: var(--accent);
    color: #000;
    font-size: 0.7rem;
    font-weight: 700;
  }

  /* ── Style drill-down (step 2) ───────────────────────────────────── */
  .genre-drill {
    text-align: left;
  }

  .genre-back {
    display: inline-flex;
    align-items: center;
    gap: 0.3rem;
    background: none;
    border: none;
    color: var(--text-secondary);
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    padding: 0.25rem 0;
    margin-bottom: 1rem;
    transition: color 0.2s;
  }

  .genre-back:hover {
    color: var(--text);
  }

  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.5rem 1rem;
    border-radius: 100px;
    border: 1.5px solid var(--border);
    background: transparent;
    color: var(--text-secondary);
    font-size: 0.9rem;
    font-weight: 500;
    transition: all 0.2s ease;
    user-select: none;
  }

  .chip:hover:not(.disabled) {
    border-color: var(--accent);
    color: var(--accent);
  }

  .chip.selected {
    background: var(--accent);
    border-color: var(--accent);
    color: #000;
    font-weight: 600;
  }

  .chip.disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }

  .check-icon {
    flex-shrink: 0;
  }

  .chip-artists {
    font-size: 0.7rem;
    color: var(--text-muted);
    font-weight: 400;
    margin-left: 0.25rem;
    opacity: 0.7;
  }
  .chip.selected .chip-artists {
    color: rgba(0, 0, 0, 0.5);
  }

  .pick-count {
    margin-top: 1.5rem;
    font-size: 0.85rem;
    color: var(--text-muted);
    transition: color 0.2s;
  }

  .pick-count.warn {
    color: var(--gold);
  }

  .cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 1.5rem;
    padding: 0.9rem 2.5rem;
    font-size: 1.1rem;
    font-weight: 700;
    border: none;
    border-radius: var(--radius);
    background: var(--cyan);
    color: #000;
    cursor: pointer;
    transition:
      opacity 0.2s,
      transform 0.15s;
    min-width: 200px;
  }

  .cta:hover:not(:disabled) {
    opacity: 0.9;
    transform: translateY(-1px);
  }

  .cta:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
  }

  .btn-spinner {
    width: 18px;
    height: 18px;
    border: 2.5px solid rgba(0, 0, 0, 0.2);
    border-top-color: #000;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  .connect-section {
    margin-top: 1.5rem;
    font-size: 0.85rem;
    color: var(--text-muted);
    text-align: center;
  }
  .btn-link {
    background: none;
    border: none;
    color: var(--text-secondary);
    text-decoration: underline;
    cursor: pointer;
    font-size: 0.85rem;
    padding: 0;
    transition: color 0.2s;
  }
  .btn-link:hover {
    color: var(--text);
  }
  .text-muted {
    color: var(--text-muted);
  }

  .connect-hint {
    font-size: 0.8rem;
    color: var(--text-muted);
    margin: 0 0 0.6rem;
  }

  .connect-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    justify-content: center;
    margin-bottom: 0.75rem;
  }

  .btn-social {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.45rem 1rem;
    border-radius: 100px;
    border: 1.5px solid var(--border);
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition:
      opacity 0.2s,
      border-color 0.2s;
    background: var(--surface);
    color: var(--text);
  }

  .btn-social:hover:not(:disabled) {
    opacity: 0.85;
  }

  .btn-social:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .btn-social--apple {
    border-color: #fc3c44;
    color: #fc3c44;
  }

  .btn-social--spotify {
    border-color: #1db954;
    color: #1db954;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .celebration {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    animation: fadeIn 0.4s ease-out;
  }
  .celebration-icon {
    font-size: 4rem;
    animation: bounce 0.6s ease-out;
  }
  .celebration-heading {
    font-size: 1.75rem;
    font-weight: 800;
    color: var(--text);
    margin: 1rem 0 0.5rem;
  }
  .celebration-text {
    color: var(--text-secondary);
    font-size: 1.1rem;
    margin: 0 0 2rem;
  }
  .celebration-bar {
    width: 200px;
    height: 4px;
    background: var(--surface-2);
    border-radius: 2px;
    overflow: hidden;
  }
  .celebration-fill {
    width: 100%;
    height: 100%;
    background: var(--cyan);
    border-radius: 2px;
    animation: fillBar 2s ease-in-out forwards;
  }
  @keyframes bounce {
    0% {
      transform: scale(0.3);
      opacity: 0;
    }
    50% {
      transform: scale(1.15);
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  @keyframes fillBar {
    from {
      width: 0;
    }
    to {
      width: 100%;
    }
  }

  .welcome-card {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    text-align: left;
    max-width: 420px;
    margin: 0 auto;
    padding: 1.5rem;
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
  }
  .welcome-field {
    display: block;
    border: none;
    padding: 0;
    margin: 0;
  }
  .welcome-label {
    display: block;
    font-size: 0.875rem;
    color: var(--text-muted);
    margin-bottom: 0.35rem;
  }
  .welcome-input {
    width: 100%;
    padding: 0.55rem 0.75rem;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 8px;
    color: var(--text);
    font-size: 0.95rem;
  }
  .welcome-input:focus {
    outline: none;
    border-color: var(--cyan);
  }
  .dob-row {
    display: flex;
    gap: 0.5rem;
  }
  .dob-select {
    flex: 1;
    min-width: 0;
  }
  .hint {
    display: block;
    margin-top: 0.35rem;
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .welcome-check {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 0.5rem;
    font-size: 0.85rem;
    color: var(--text-muted);
    line-height: 1.4;
  }
  .welcome-check input[type='checkbox'] {
    margin-top: 0.15rem;
    flex-shrink: 0;
  }
  .welcome-check a {
    color: var(--cyan);
    text-decoration: underline;
  }
  .welcome-check a:hover {
    text-decoration: none;
  }

  @media (max-width: 480px) {
    .heading {
      font-size: 1.75rem;
    }

    .subtitle {
      font-size: 1rem;
      margin-bottom: 2rem;
    }

    .chip {
      font-size: 0.85rem;
      padding: 0.4rem 0.85rem;
    }

    .cta {
      width: 100%;
    }
  }
</style>
